import { createContext, useContext, useState, useEffect, useCallback } from "react";

const translations = {
  en: {
    nav: {
      notice: "EXCLUSIVE PORTAL FOR WHOLESALE BUYERS · NATIONWIDE SHIPPING · 24–48H DISPATCH",
      tagline: "Distribution",
      links: {
        inicio: "Home",
        catalogo: "Catalog",
        marcas: "Brands",
        ventajas: "B2B Benefits",
        registro: "Open Account",
        contacto: "Contact",
      },
      admin: "Admin Access",
      cta: "Open Account",
    },
    hero: {
      badge: "Direct distributor pricing",
      titleLines: ["Wholesale", "Supplier of", "Volume"],
      desc: "We stock your business with high-demand products, volume-based tiered pricing and fast dispatch logistics for retailers and resellers.",
      ctaPrimary: "Request Catalog Access",
      ctaSecondary: "Talk to an Advisor",
      metrics: [
        { v: "+10,000", l: "Available SKUs" },
        { v: "24–48H", l: "Order Dispatch" },
        { v: "100%", l: "Quality Guarantee" },
      ],
      verified: "Verified B2B Account",
    },
    ribbon: ["WHOLESALE LINE", "B2B SOLUTIONS", "VOLUME PRICING", "24–48H DISPATCH", "NATIONWIDE DISTRIBUTION"],
    categories: {
      kicker: "/ Catalog",
      headingA: "Product lines",
      headingB: "with immediate availability",
      sub: "Explore our categories with stock ready for pallets or closed cases.",
      cta: "View catalog",
      items: [
        { title: "Wholesale Line A", desc: "High-turnover commercial products designed to maximize your profit margin.", tag: "High rotation" },
        { title: "Wholesale Line B", desc: "Specialized inventory ready for local distribution and e-commerce sales.", tag: "Specialized" },
        { title: "Bulk / Pallets", desc: "Mass-volume deals and exclusive clearances for large distributors.", tag: "Bulk volume" },
        { title: "PET Line", desc: "Food, treats and accessories for pets from the market's most-demanded brands.", tag: "Pets" },
      ],
    },
    brands: {
      kicker: "/ Brands",
      headingA: "Our brand catalog,",
      headingB: "sheet by sheet",
      sub: "Flip through the leading brands we distribute in each wholesale line. Over 45 leading brands.",
      available: "Available brands",
      countSuffix: "brands in this line",
      pageWord: "Sheet",
      subtitles: ["Mass Consumer & Groceries", "Electronics & E-commerce", "Home & Tools", "Pets · Food & Accessories"],
      categoryNames: ["Wholesale Line A", "Wholesale Line B", "Bulk / Pallets", "PET Line"],
      lineWord: "Line",
    },
    manifesto: {
      kicker: "/ B2B Benefits",
      heading: "Why work with us",
      chapters: [
        { title: "Volume discounts", body: "The more you buy, the lower your cost per unit. Our tiered pricing structure rewards every extra pallet you add to your order." },
        { title: "Inventory management", body: "View real-time stock, UPC/SKU data and availability by category before confirming each wholesale order." },
        { title: "Priority support", body: "A dedicated account executive manages your orders, resolves issues and coordinates dispatch logistics with you." },
      ],
      note: "Access to full pricing and catalog requires verification of a registered company or business.",
      caption: "Logistics center · 24/7 operation",
    },
    form: {
      kicker: "/ Registration",
      heading: "Open your wholesale account",
      sub: "Complete the information to verify your company and receive our price list.",
      fields: {
        company_name: "Company / Business Name",
        tax_id: "Tax ID / Resale Permit",
        email: "Business Email",
        phone: "Contact Phone",
      },
      placeholders: {
        company_name: "Your Company LLC / Inc",
        tax_id: "Business registration number",
        email: "sales@yourcompany.com",
        phone: "+1 (555) 000-0000",
      },
      volumeLabel: "Estimated Monthly Purchase Volume",
      submit: "Submit B2B Registration Request",
      sending: "Sending…",
      successTitle: "Request received",
      successBody: "We've registered your B2B request. An account executive will verify your company and send you the catalog with wholesale pricing within the next 24 hours.",
      another: "Submit another request",
      toastSuccess: "Request sent. Our team will contact you soon.",
      toastError: "The request could not be sent.",
    },
    footer: {
      desc: "High-volume B2B wholesale distribution for retailers and resellers. B2B sales only.",
      contact: "Contact",
      nav: "Navigation",
      legal: "Legal",
      links: { catalogo: "Catalog", marcas: "Brands", ventajas: "B2B Benefits", registro: "Open Account", admin: "Admin Access", terms: "B2B Terms", shipping: "Shipping Policy" },
      copy: "© 2026 ESOESLY. All rights reserved.",
      tagline: "B2B sales only · Nationwide shipping",
    },
    admin: {
      back: "Back to site",
      panel: "Admin Panel",
      panelSub: "Restricted access for wholesale administrators.",
      email: "Email",
      password: "Password",
      login: "Sign In",
      verifying: "Verifying…",
      loggedIn: "Signed in",
      loginError: "Sign-in error",
      loggedOut: "Signed out",
      panelTop: "B2B Panel",
      title: "Wholesale Requests",
      subtitle: "B2B lead management and account verification.",
      stats: { total: "Total", nuevo: "New", contactado: "Contacted", aprobado: "Approved" },
      search: "Search company or email…",
      logout: "Log out",
      cols: { company: "Company", tax: "Tax ID", contact: "Contact", volume: "Volume", status: "Status", date: "Date" },
      loading: "Loading…",
      empty: "No requests registered yet.",
      statusLabels: { nuevo: "New", contactado: "Contacted", aprobado: "Approved" },
      loadError: "Could not load requests",
      updateError: "Could not update",
      statusTo: "Status →",
    },
    lang: { switch: "ES" },
  },
  es: {
    nav: {
      notice: "PORTAL EXCLUSIVO PARA COMPRADORES MAYORISTAS · ENVÍOS A TODO EL PAÍS · DESPACHO 24–48H",
      tagline: "Distribución",
      links: {
        inicio: "Inicio",
        catalogo: "Catálogo",
        marcas: "Marcas",
        ventajas: "Ventajas B2B",
        registro: "Abrir Cuenta",
        contacto: "Contacto",
      },
      admin: "Acceso Admin",
      cta: "Abrir Cuenta",
    },
    hero: {
      badge: "Precios directos de distribuidor",
      titleLines: ["Proveedor", "Mayorista de", "Volumen"],
      desc: "Surtimos tu negocio con productos de alta demanda, precios escalonados por volumen y logística de despacho rápido para minoristas y revendedores.",
      ctaPrimary: "Solicitar Acceso al Catálogo",
      ctaSecondary: "Hablar con un Asesor",
      metrics: [
        { v: "+10,000", l: "SKUs Disponibles" },
        { v: "24–48H", l: "Despacho de Pedidos" },
        { v: "100%", l: "Garantía de Calidad" },
      ],
      verified: "Cuenta B2B Verificada",
    },
    ribbon: ["LÍNEA MAYORISTA", "SOLUCIONES B2B", "PRECIOS POR VOLUMEN", "DESPACHO 24–48H", "DISTRIBUCIÓN NACIONAL"],
    categories: {
      kicker: "/ Catálogo",
      headingA: "Líneas de producto",
      headingB: "con disponibilidad inmediata",
      sub: "Explora nuestras categorías con stock listo para palets o cajas cerradas.",
      cta: "Ver catálogo",
      items: [
        { title: "Línea Mayorista A", desc: "Productos de alta rotación comercial diseñados para maximizar tu margen de ganancia.", tag: "Alta rotación" },
        { title: "Línea Mayorista B", desc: "Inventario especializado listo para distribución local y venta e-commerce.", tag: "Especializado" },
        { title: "Lotes / Palets", desc: "Ofertas por volumen masivo y liquidaciones exclusivas para grandes distribuidores.", tag: "Volumen masivo" },
        { title: "Línea PET", desc: "Alimento, snacks y accesorios para mascotas de las marcas más demandadas del mercado.", tag: "Mascotas" },
      ],
    },
    brands: {
      kicker: "/ Marcas",
      headingA: "Nuestro catálogo",
      headingB: "de marcas, hoja por hoja",
      sub: "Hojea las principales marcas que distribuimos en cada línea mayorista. Más de 45 marcas líderes.",
      available: "Marcas disponibles",
      countSuffix: "marcas en esta línea",
      pageWord: "Hoja",
      subtitles: ["Consumo masivo & Abarrotes", "Electrónica & E-commerce", "Hogar & Herramientas", "Mascotas · Alimento & Accesorios"],
      categoryNames: ["Línea Mayorista A", "Línea Mayorista B", "Lotes / Palets", "Línea PET"],
      lineWord: "Línea",
    },
    manifesto: {
      kicker: "/ Ventajas B2B",
      heading: "Por qué operar con nosotros",
      chapters: [
        { title: "Descuentos por escala", body: "A mayor volumen, menor costo por unidad. Nuestra estructura de precios escalonada premia cada palet adicional que sumas a tu orden." },
        { title: "Gestión de inventario", body: "Visualiza stock en tiempo real, datos UPC/SKU y disponibilidad por categoría antes de confirmar cada pedido mayorista." },
        { title: "Atención prioritaria", body: "Un ejecutivo de cuenta asignado gestiona tus órdenes, resuelve incidencias y coordina la logística de despacho contigo." },
      ],
      note: "El acceso a precios y catálogo completo requiere verificación de empresa o negocio registrado.",
      caption: "Centro logístico · Operación 24/7",
    },
    form: {
      kicker: "/ Registro",
      heading: "Abre tu cuenta mayorista",
      sub: "Completa la información para verificar tu empresa y enviarte nuestra lista de precios.",
      fields: {
        company_name: "Nombre de la Empresa / Negocio",
        tax_id: "Tax ID / Permiso de Reventa",
        email: "Correo Electrónico Comercial",
        phone: "Teléfono de Contacto",
      },
      placeholders: {
        company_name: "Tu Empresa LLC / Inc",
        tax_id: "Número de registro comercial",
        email: "ventas@tuempresa.com",
        phone: "+1 (555) 000-0000",
      },
      volumeLabel: "Volumen Estimado de Compra Mensual",
      submit: "Enviar Solicitud de Registro B2B",
      sending: "Enviando…",
      successTitle: "Solicitud recibida",
      successBody: "Hemos registrado tu solicitud B2B. Un ejecutivo de cuenta verificará tu empresa y te enviará el catálogo con precios mayoristas en las próximas 24 horas.",
      another: "Enviar otra solicitud",
      toastSuccess: "Solicitud enviada. Nuestro equipo te contactará pronto.",
      toastError: "No se pudo enviar la solicitud.",
    },
    footer: {
      desc: "Distribución mayorista de alto volumen para minoristas y revendedores. Venta exclusiva B2B.",
      contact: "Contacto",
      nav: "Navegación",
      legal: "Legal",
      links: { catalogo: "Catálogo", marcas: "Marcas", ventajas: "Ventajas B2B", registro: "Abrir Cuenta", admin: "Acceso Admin", terms: "Términos B2B", shipping: "Política de Envíos" },
      copy: "© 2026 ESOESLY. Todos los derechos reservados.",
      tagline: "Venta exclusiva B2B · Envíos a todo el país",
    },
    admin: {
      back: "Volver al sitio",
      panel: "Panel Admin",
      panelSub: "Acceso restringido a administradores mayoristas.",
      email: "Correo",
      password: "Contraseña",
      login: "Iniciar Sesión",
      verifying: "Verificando…",
      loggedIn: "Sesión iniciada",
      loginError: "Error al iniciar sesión",
      loggedOut: "Sesión cerrada",
      panelTop: "Panel B2B",
      title: "Solicitudes Mayoristas",
      subtitle: "Gestión de leads B2B y verificación de cuentas.",
      stats: { total: "Total", nuevo: "Nuevos", contactado: "Contactados", aprobado: "Aprobados" },
      search: "Buscar empresa o correo…",
      logout: "Salir",
      cols: { company: "Empresa", tax: "Tax ID", contact: "Contacto", volume: "Volumen", status: "Estado", date: "Fecha" },
      loading: "Cargando…",
      empty: "Aún no hay solicitudes registradas.",
      statusLabels: { nuevo: "Nuevo", contactado: "Contactado", aprobado: "Aprobado" },
      loadError: "No se pudieron cargar las solicitudes",
      updateError: "No se pudo actualizar",
      statusTo: "Estado →",
    },
    lang: { switch: "EN" },
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("wh_lang") || "en");
  useEffect(() => {
    localStorage.setItem("wh_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const toggle = useCallback(() => setLang((l) => (l === "en" ? "es" : "en")), []);
  return <I18nContext.Provider value={{ lang, setLang, toggle, t: translations[lang] }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
